// chessThemes.js

export const BOARD_THEMES = {
  classic: {
    id: "classic",
    name: "Classic",
    description: "Traditional cream and walnut.",
  },
  midnight: {
    id: "midnight",
    name: "Midnight",
    description: "Dark graphite with cool highlights.",
  },
  grandmaster: {
    id: "grandmaster",
    name: "Grandmaster",
    description: "Ivory, dark wood, and gold trim.",
  },
  hobbliPop: {
    id: "hobbli-pop",
    name: "Hobbli Pop",
    description: "Brighter, rounder, and more playful.",
  },
};

export const PIECE_SETS = {
  classic: {
    id: "classic",
    name: "Classic",
    description: "Traditional chess glyphs.",
  },
  modern: {
    id: "modern",
    name: "Modern",
    description: "Sharper, cleaner, high-contrast pieces.",
  },
  pop: {
    id: "pop",
    name: "Hobbli Pop",
    description: "Chunkier, friendlier game-room styling.",
  },
};

export const RULE_EXPLANATIONS = [
  {
    id: "check",
    title: "Check",
    short: "Your king is currently under attack.",
    body:
      "When your king is in check, your next move must remove the threat. You may move the king, capture the attacking piece, or block the attack when blocking is possible.",
  },
  {
    id: "checkmate",
    title: "Checkmate",
    short: "Check with no legal escape ends the game.",
    body:
      "Checkmate happens when the king is under attack and there is no legal move that removes the attack. The player delivering checkmate wins immediately.",
  },
  {
    id: "castling",
    title: "Castling",
    short: "A special king-and-rook move.",
    body:
      "Castling moves the king two squares toward a rook and places that rook beside the king. The king and rook must not have moved before, the squares between them must be empty, and the king cannot castle out of, through, or into check.",
  },
  {
    id: "en-passant",
    title: "En Passant",
    short: "A special pawn capture after a two-square pawn move.",
    body:
      "If an opposing pawn advances two squares from its starting position and lands directly beside your pawn, your pawn may capture it as though it had advanced only one square. This option exists only on the immediately following move.",
  },
  {
    id: "promotion",
    title: "Pawn Promotion",
    short: "A pawn reaching the far side becomes a stronger piece.",
    body:
      "When a pawn reaches the last rank, it must be promoted to a queen, rook, bishop, or knight. Promotion to a queen is most common, but underpromotion to another piece is legal.",
  },
  {
    id: "stalemate",
    title: "Stalemate",
    short: "No legal move, but the king is not in check.",
    body:
      "If the player whose turn it is has no legal move but is not in check, the game is a draw rather than a loss.",
  },
  {
    id: "threefold",
    title: "Threefold Repetition",
    short: "The same position occurs three times.",
    body:
      "A player may claim a draw when the same position occurs three times with the same player to move and the same relevant castling and en-passant rights. Hobbli exposes a Claim Draw button when this condition is met.",
  },
  {
    id: "fifty-move",
    title: "50-Move Rule",
    short: "50 moves by each side without a pawn move or capture.",
    body:
      "A player may claim a draw after 50 moves by each player occur without any pawn move or capture. Hobbli tracks this automatically and offers a Claim Draw button when eligible.",
  },
  {
    id: "automatic-draws",
    title: "Automatic Draws",
    short: "Some draw conditions do not require a claim.",
    body:
      "Hobbli automatically ends the game after fivefold repetition, after 75 moves by each side without a pawn move or capture, or when the remaining material is insufficient to force checkmate.",
  },
];

export function loadChessAppearance() {
  if (typeof window === "undefined") {
    return {
      boardTheme: "classic",
      pieceSet: "modern",
      showCoordinates: true,
    };
  }

  try {
    const raw = window.localStorage.getItem(
      "hobbliChessAppearance"
    );

    if (!raw) {
      return {
        boardTheme: "classic",
        pieceSet: "modern",
        showCoordinates: true,
      };
    }

    const parsed = JSON.parse(raw);

    return {
      boardTheme:
        Object.values(BOARD_THEMES).some(
          (theme) => theme.id === parsed.boardTheme
        )
          ? parsed.boardTheme
          : "classic",
      pieceSet:
        Object.values(PIECE_SETS).some(
          (set) => set.id === parsed.pieceSet
        )
          ? parsed.pieceSet
          : "modern",
      showCoordinates:
        parsed.showCoordinates !== false,
    };
  } catch {
    return {
      boardTheme: "classic",
      pieceSet: "modern",
      showCoordinates: true,
    };
  }
}

export function saveChessAppearance(value) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(
      "hobbliChessAppearance",
      JSON.stringify(value)
    );
  } catch {
    // Appearance persistence is a convenience only.
  }
}
